import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../api/apiService';

function Profile() {
    const [currentPassword, setCurrentPassword] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [detailedError, setDetailedError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [passwordChanged, setPasswordChanged] = useState(false);
    const { currentUser } = useAuth();

    const isValidPassword = (pass) => {
        return (
            pass.length >= 8 &&                      // Minimum 8 characters
            /[A-Z]/.test(pass) &&                    // At least one uppercase character
            /[^a-zA-Z]/.test(pass)                   // At least one non-letter character
        );
    }

    const updatePasswordFunction = async () => {
        // Reset messages
        setMessage('');
        setError('');
        setDetailedError('');
        setIsSubmitting(true);
        
        // Check if current password is provided
        if (!currentPassword) {
            setError("Current password is required.");
            setIsSubmitting(false);
            return;
        }
        
        // Validate password
        if (!isValidPassword(password)) {
            setError("Password must be at least 8 characters long, contain at least one uppercase letter and one non-letter character.");
            setIsSubmitting(false);
            return;
        }
        
        // Check if passwords match
        if (password !== confirmPassword) {
            setError("Passwords do not match!");
            setIsSubmitting(false);
            return;
        }
    
        try {
            console.log("Attempting to update password with:", {
                email: currentUser.email,
                currentPassword: "***", // Don't log actual password
                newPasswordLength: password.length
            });
            
            // Create a separate axios instance to prevent global interceptor from redirecting
            const passwordUpdateData = { 
                email: currentUser.email,
                currentPassword: currentPassword,
                newPassword: password 
            };
            
            // Call API to update password
            const response = await authService.updatePassword(passwordUpdateData);
            
            console.log("Password update response:", response);
            
            // Set success state and clear form
            setPasswordChanged(true);
            setMessage("Password successfully updated! You'll need to re-login with your new password on your next visit.");
            
            // Clear the form fields
            setCurrentPassword('');
            setPassword('');
            setConfirmPassword('');
        } catch (error) {
            console.error("Error updating password:", error);
            
            // Extract detailed error information
            const errorMessage = error.response?.data?.message || "Unknown error";
            const statusCode = error.response?.status;
            const responseData = error.response?.data;
            
            // Log detailed error for debugging
            console.error("Error details:", {
                message: errorMessage,
                status: statusCode,
                data: responseData,
                error: error.toString()
            });
            
            // Handle incorrect current password specifically
            if (statusCode === 400 && errorMessage.includes("current password")) {
                setError("The current password you entered is incorrect. Please try again.");
            } else {
                // Set user-friendly error
                setError(`Error updating password: ${errorMessage}`);
            }
            
            // Set technical details for debugging
            setDetailedError(
                `Status: ${statusCode}, 
                Response: ${JSON.stringify(responseData, null, 2)}, 
                Error: ${error.toString()}`
            );
        } finally {
            setIsSubmitting(false);
        }
    }
    
    return (
        <div className="content-wrapper page">
            <section>
                <h1 style={{ marginBottom: '40px' }}>Profile</h1>
                <h2 style={{ marginBottom: '40px' }}>Update Password</h2>
                <div style={{ maxWidth: '500px' }}>
                    {message && (
                        <div style={{ 
                            backgroundColor: '#d4edda', 
                            color: '#155724', 
                            padding: '15px', 
                            borderRadius: '4px',
                            marginBottom: '20px' 
                        }}>
                            <p>{message}</p>
                        </div>
                    )}
                    
                    {error && (
                        <div style={{ 
                            backgroundColor: '#f8d7da', 
                            color: '#721c24', 
                            padding: '15px', 
                            borderRadius: '4px',
                            marginBottom: '15px' 
                        }}>
                            <p><strong>Error:</strong> {error}</p>
                            {detailedError && (
                                <div style={{ 
                                    backgroundColor: '#f8f9fa',
                                    color: '#212529',
                                    padding: '10px',
                                    marginTop: '10px',
                                    fontSize: '0.9em',
                                    fontFamily: 'monospace',
                                    whiteSpace: 'pre-wrap',
                                    overflowX: 'auto'
                                }}>
                                    <p><strong>Technical Details:</strong></p>
                                    <p>{detailedError}</p>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <div style={{ marginBottom: '20px' }}>
                        <label htmlFor="current-password" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Current Password:
                        </label>
                        <input 
                            id="current-password"
                            className='inputtext'
                            type="password"
                            placeholder="Enter current password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                        />
                    </div>
                    
                    <div style={{ marginBottom: '20px' }}>
                        <label htmlFor="new-password" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            New Password:
                        </label>
                        <input 
                            id="new-password"
                            className='inputtext'
                            type="password"
                            placeholder="Enter new password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </div>
                    
                    <div style={{ marginBottom: '20px' }}>
                        <label htmlFor="confirm-password" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Confirm New Password:
                        </label>
                        <input 
                            id="confirm-password"
                            className='inputtext'
                            type="password"
                            placeholder="Confirm new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                    </div>
                    
                    <button 
                        style={{ marginTop: '20px' }}
                        className='big-button' 
                        onClick={updatePasswordFunction}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Updating...' : 'Update Password'}
                    </button>
                    
                    {/* User info for debugging */}
                    <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                        <h3>Current User Information</h3>
                        <p><strong>Email:</strong> {currentUser?.email || 'Not available'}</p>
                        <p><strong>User ID:</strong> {currentUser?.uid || 'Not available'}</p>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default Profile;
